import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { gsap } from "gsap/dist/gsap";
import React, { forwardRef, useId, useRef } from "react";

import {
    SplitPanelCardBlock,
    SplitPanelCardsBlock as Value,
} from "@reactivated";

import { MediaChooserBlock } from "../MediaChooserBlock";

import styles from "./index.module.scss";

const SplitPanelCard = forwardRef<HTMLDivElement, SplitPanelCardBlock>(
    ({ detail_media: media, title, subtitle, feature_media }, ref) => {
        if (!feature_media) return <></>;
        return (
            <div ref={ref} className={styles.wrapper}>
                <div
                    data-feature-media
                    key={feature_media[0].id}
                    className={styles.featureMedia}
                >
                    <MediaChooserBlock
                        value={feature_media}
                        type="background"
                        mediaAttrs={
                            {
                                loading:
                                    feature_media[0].type === "image"
                                        ? "eager"
                                        : undefined,
                            } as React.ImgHTMLAttributes<HTMLImageElement>
                        }
                    />
                </div>
                <div data-card className={styles.card}>
                    {media && media.length > 0 ? (
                        <div className={styles.cardMedia}>
                            <MediaChooserBlock
                                value={media}
                                type="background"
                            />
                        </div>
                    ) : null}
                    <h3 className={styles.cardTitle}>{title}</h3>
                    <p className={styles.cardSubtitle}>{subtitle}</p>
                </div>
            </div>
        );
    },
);

interface Props {
    value: Value;
}

export const SplitPanelCardsBlock = ({ value: { cards, theme } }: Props) => {
    const id = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const cardWrapperRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const dummyCardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useGSAP(() => {
        if (
            !cardWrapperRef.current ||
            cardRefs.current.length === 0 ||
            dummyCardRefs.current.length === 0
        )
            return;

        /**
         * I either missed something obvious or got a hard use case.
         * I wanted a ScrollTrigger to trigger transitions, but I didn't want
         * snapping, I didn't want scrubbing, and I wanted a clean crossfade
         * which meant that the cards needed to be stacked on each other, which
         * (I think) meant that I couldn't use the cards themselves to trigger
         * the transitions.
         *
         * So I made dummy triggers that operate in ScrollTrigger's assumptions,
         * and use them to trigger the card crossfades.
         */
        const dummyTriggers = dummyCardRefs.current;

        /**
         * I _would_ do this on cardRefs directly, except that featureMedia
         * crossfades look way better when the active media and everything under
         * it are visible. So it's not a true crossfade, it's a new piece
         * appearing on top of the last one.
         */
        const featureMedia = cardRefs.current.map((card) =>
            card?.querySelector(`[data-feature-media]`),
        );

        // We want a true crossfade here.
        const cards = cardRefs.current.map((card) =>
            card?.querySelector(`[data-card]`),
        );

        // Hide all card/media content
        gsap.set(cards, { opacity: 0 });
        gsap.set(featureMedia, { opacity: 0 });

        // Then show the first by default
        if (cards[0]) gsap.set(cards[0], { opacity: 1 });
        if (featureMedia[0]) gsap.set(featureMedia[0], { opacity: 1 });

        // The whole section needs to be pinned to keep the stacked cards in view
        ScrollTrigger.create({
            trigger: cardWrapperRef.current,
            start: "top top",
            endTrigger: dummyTriggers.at(-1) ?? cardWrapperRef.current,
            end: "bottom bottom",
            pin: true,
        });

        dummyTriggers.forEach((element, index) => {
            if (!element) return;

            const duration = 0.3;

            const updateCards = () => {
                if (!cards[index] || !featureMedia[index]) return;

                // If index is 3, then this hides all of the cards then
                // shows the third one
                gsap.to(cards, { opacity: 0, duration });
                gsap.to(cards[index], { opacity: 1, duration });

                // Then, this ensures that cards 1-3 are visible while anything
                // after is hidden
                featureMedia.forEach((media, i) => {
                    if (!media) return;
                    gsap.to(media, { opacity: i <= index ? 1 : 0, duration });
                });

                // Finally, update a CSS var that we'll use to show a progress bar
                const percent = (index / (cards.length - 1)) * 100;
                rootRef.current?.style.setProperty("--progress", `${percent}%`);
            };

            ScrollTrigger.create({
                trigger: element,
                start: "top top",
                onEnter: updateCards,
                onEnterBack: updateCards,
            });
        });

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };
    }, []);

    if (!cards) return <></>;

    return (
        <section ref={rootRef} className={styles[theme ?? "match"]}>
            <div ref={cardWrapperRef}>
                {cards.map((card, index) => (
                    <SplitPanelCard
                        ref={(element) => {
                            cardRefs.current[index] = element;
                        }}
                        key={`${id}-card-${index}`}
                        {...card}
                    />
                ))}
            </div>

            {Array.from({ length: cards.length + 1 }).map((_, index) => (
                <div
                    className={styles.dummyCard}
                    ref={(element) => {
                        dummyCardRefs.current[index] = element;
                    }}
                    key={`${id}-dummy-card-${index}`}
                />
            ))}
        </section>
    );
};
